import { Box, Button, Flex, HStack, Text, useDisclosure, useToast, VStack } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { FieldError, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import * as yup from 'yup'
import { Input } from '../../../../../components/Form/Input'
import { Step } from '../../../../../Enuns/step.enum'
import { Product } from '../../../../../Interfaces/products'
import { RawMaterial } from '../../../../../Interfaces/rawMaterial'
import { ServiceRawMaterials } from '../../../../../Interfaces/serviceRawMaterials'
import { WorkSchedule } from '../../../../../Interfaces/workSchedule'
import { api } from '../../../../../services/api'
import { RawMaterialSelectionModal } from '../components/RawMaterialSelectionModal'

const createServiceOrderSchema = yup.object().shape({
  raw_material: yup.string(),
  spent_amount: yup.string().required('Informe a quantidade'),
})

interface Props {
  selectedRawMaterial?: ServiceRawMaterials
  orderId: string
  onClose: () => void
  step: Step
}

export default function RawMaterialServiceCreateOrUpdate({ selectedRawMaterial, onClose, orderId, step }: Props) {
  const queryClient = useQueryClient()
  const toast = useToast()

  const id = selectedRawMaterial?.id ?? ''

  const { register, handleSubmit, formState, reset, setValue, watch } = useForm({
    resolver: yupResolver(createServiceOrderSchema),
  })

  const errors = formState.errors

  const isCreateMode = !id

  // Estados para o modal de seleção
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure()
  const [selectedRawMaterialData, setSelectedRawMaterialData] = useState<RawMaterial | null>(null)
  const [rawMaterialsList, setRawMaterialsList] = useState<RawMaterial[]>([])

  const selectedRawMaterialId = watch('raw_material')

  const createDemand = useMutation(
    async (rawMaterial: any) => {
      const workSchedule = {
        spent_amount: rawMaterial.spent_amount,
        step,
      }

      const response = await api.post<Product>(`/service-orders/${orderId}/raw-materials/${rawMaterial.raw_material}`, {
        ...workSchedule,
      })

      if (response) {
        toast({ description: 'Cadastrado com sucesso.' })

        onClose()
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('serviceOrderProduct')
        queryClient.refetchQueries()
      },
      onError: ({ response }) => {
        const errorList = response?.data.errors

        if (errorList) {
          const toastOptions = errorList.map((err) => ({ description: err.message, status: 'error' }))
          toastOptions.forEach((t) => {
            toast(t)
          })
        } else {
          if (response.data.error) {
            toast({ description: response.data.error.developer_message, status: 'error' })
          } else {
            toast({ description: response.data.message, status: 'error' })
          }
        }
      },
    },
  )

  useEffect(() => {
    if (id) {
      reset({ ...selectedRawMaterial })
    }
  }, [])

  const updateDemands = useMutation(
    async (rawMaterial: any) => {
      const workSchedule = {
        spent_amount: rawMaterial.spent_amount,
        step,
      }

      const response = await api.put<Product>(`/service-orders/${orderId}/raw-materials/${selectedRawMaterial.id}`, {
        ...workSchedule,
      })

      if (response) {
        toast({ description: 'Editado com sucesso.' })

        onClose()
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('serviceOrderProduct')
        queryClient.refetchQueries()
      },
      onError: ({ response }) => {
        const errorList = response?.data.errors

        if (errorList) {
          const toastOptions = errorList.map((err) => ({ description: err.message, status: 'error' }))
          toastOptions.forEach((t) => {
            toast(t)
          })
        } else {
          if (response.data.error) {
            toast({ description: response.data.error.developer_message, status: 'error' })
          } else {
            toast({ description: response.data.message, status: 'error' })
          }
        }
      },
    },
  )

  const handleCreateOrUpdate: SubmitHandler<WorkSchedule> = async (values) => {
    isCreateMode ? await createDemand.mutateAsync(values) : await updateDemands.mutateAsync(values)
  }

  const { data: rawMaterials } = useQuery(
    'rawMaterials',
    async () => {
      const response = await api.get<{ raw_materials: RawMaterial[] } | RawMaterial[]>(`raw-materials?is_enable=true`)

      // Verifica se a resposta tem paginação ou é uma lista direta
      const materials = (response.data as any).raw_materials || response.data

      // Filtrar apenas matérias-primas ativas (is_enable = true)
      const activeMaterials = materials.filter((material) => material.is_enable === true)

      // Primeiro, ordena os dados recebidos pela propriedade desejada (ex.: 'name')
      const sortedData = activeMaterials.sort((a, b) => a.name.localeCompare(b.name))

      // Armazenar a lista completa para o modal
      setRawMaterialsList(sortedData)

      // Depois, mapeia os dados ordenados para o formato esperado pelo Select
      const selectOptions = sortedData.map((x) => ({
        label: `${x.name} - ${x.amount} ${x.measurement_unit}`,
        value: x.id,
      }))

      return { rawMaterials: selectOptions }
    },
    { retry: false },
  )

  // Função para abrir o modal de seleção
  const handleOpenModal = () => {
    onModalOpen()
  }

  // Função para selecionar matéria-prima do modal
  const handleSelectRawMaterial = (rawMaterial: RawMaterial) => {
    setSelectedRawMaterialData(rawMaterial)
    setValue('raw_material', rawMaterial.id.toString())
    onModalClose()
  }

  return (
    <Box flex="1" borderRadius="8" bg="gray.800" as="form" onSubmit={handleSubmit(handleCreateOrUpdate)}>
      <VStack spacing="8">
        <VStack spacing="8" width="100%">
          {!id && (
            <Box width="100%">
              <Text mb={2}>
                Matéria-prima
              </Text>
              <Button
                onClick={handleOpenModal}
                variant="outline"
                colorScheme="pink"
                width="100%"
                justifyContent="flex-start"
                height="40px"
                bg="gray.700"
                borderColor="gray.600"
                _hover={{ borderColor: 'pink.400', bg: 'gray.600' }}
                _focus={{ borderColor: 'pink.400', boxShadow: 'none' }}
                {...({} as any)}
              >
                {selectedRawMaterialData ? (
                  <Text color="white" fontSize="sm" textAlign="left">
                    {selectedRawMaterialData.name} - {selectedRawMaterialData.amount} {selectedRawMaterialData.measurement_unit}
                  </Text>
                ) : (
                  <Text color="gray.400" fontSize="sm" textAlign="left">
                    Clique para selecionar
                  </Text>
                )}
              </Button>
            </Box>
          )}

          <Box width="100%">
            <Input label="Quantidade" {...register('spent_amount')} error={errors.spent_amount as FieldError} />
          </Box>
        </VStack>
      </VStack>

      <Flex mt="8" justify="flex-end">
        <HStack spacing="4">
          <Button type="submit" colorScheme="pink" isLoading={formState.isSubmitting}>
            Salvar
          </Button>
        </HStack>
      </Flex>

      {/* Modal de seleção de matéria-prima */}
      <RawMaterialSelectionModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        onSelect={handleSelectRawMaterial}
        rawMaterials={rawMaterialsList}
      />
    </Box>
  )
}
